import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Envelope, ChartLineUp, CheckCircle,
  Pulse, Television, Bell, GlobeHemisphereWest, HardDrives,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background-deep text-white sw-grid-bg">
      {/* Top nav */}
      <header className="border-b border-border bg-background-deep/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto h-14 px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/app" data-testid="landing-open-app"
                className="px-4 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-medium transition-colors">
                Open dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login" className="px-4 py-1.5 text-sm text-slate-300 hover:text-white">Sign in</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-7">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Every backup. Every gateway.<br/>
              <span className="text-brand-cyan">Every silent failure.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              SentryWatch is the single pane of glass for SentryIT operations.
              Backup monitoring, uptime checks, ISP gateway health —
              with AI email triage and multi-channel alerts. Built for the SentryIT helpdesk wall.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/login" data-testid="hero-cta-login"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-600 hover:border-brand-cyan hover:text-brand-cyan rounded-md text-sm transition-colors">
                Sign in
              </Link>
            </div>
            <ul className="grid grid-cols-2 gap-2 text-xs text-slate-400 max-w-md pt-4">
              {[
                "Backups: any vendor",
                "Uptime: HTTP / Ping / TCP / DNS",
                "Alerts: Email, SMS, Slack, Teams",
                "Client portal sharing",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle weight="fill" size={14} className="text-emerald-400"/>{t}</li>
              ))}
            </ul>
          </div>

          {/* Mock dashboard widget */}
          <div className="lg:col-span-5">
            <div className="sw-card overflow-hidden shadow-2xl">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-background-deep">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60"/>
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60"/>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60"/>
                <span className="ml-3 text-xs text-slate-500">sentrywatch · NOC</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { l: "UP",       v: 248, c: "text-emerald-400" },
                    { l: "DEGRADED", v: 7,   c: "text-amber-400"   },
                    { l: "DOWN",     v: 3,   c: "text-red-400"     },
                  ].map((s) => (
                    <div key={s.l} className="border border-border rounded p-3 bg-white/5">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{s.l}</div>
                      <div className={`font-display text-2xl font-bold mt-1 ${s.c}`}>{s.v}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {[
                    { c: "Acme MSP",         j: "fileserver01 / Veeam",      s: "failure", t: "10:42" },
                    { c: "Northwind Trader", j: "exch-mbx-01 / Acronis",     s: "warning", t: "08:11" },
                    { c: "Globex Inc.",      j: "ISP gateway / 1.1.1.1",      s: "up",      t: "live"  },
                    { c: "Stark Industries", j: "https://app.stark.com",      s: "up",      t: "live"  },
                  ].map((r) => (
                    <div key={r.j} className="flex items-center justify-between text-xs border-b border-border last:border-0 py-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`status-dot ${r.s}`} />
                        <div className="truncate">
                          <div className="text-slate-200 truncate">{r.j}</div>
                          <div className="text-slate-500">{r.c}</div>
                        </div>
                      </div>
                      <div className="text-slate-500">{r.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border bg-background">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { I: HardDrives,     t: "Backup monitoring", d: "Email-parsed reports + vendor APIs + AI triage. Every backup, classified." },
            { I: Pulse,           t: "Uptime checks",     d: "HTTP, Ping, TCP, DNS. From websites to ISP gateways and internal IPs." },
            { I: Television,      t: "NOC TV mode",       d: "Helpdesk wall view. Failures pinned. Successes auto-cycle. Big fonts, glanceable." },
            { I: Bell,            t: "Multi-channel alerts", d: "Email, SMS, Slack, Discord, Teams, Telegram. Choose per monitor." },
          ].map(({ I, t, d }) => (
            <div key={t} className="p-6 sw-card sw-card-hover">
              <I size={28} weight="duotone" className="text-brand-cyan mb-4" />
              <h3 className="font-display text-lg font-bold tracking-tight mb-2 text-white">{t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Client portal */}
      <section className="border-t border-border bg-background-deep">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-cyan/30 rounded-full text-[11px] text-brand-cyan font-semibold uppercase tracking-[0.15em] mb-4">
              <ShieldCheck size={12} weight="fill" />
              SHARE WITHOUT EXPOSING
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white">
              Give every client their own read-only window.
            </h2>
            <p className="text-slate-300 text-base leading-relaxed mb-6">
              Create a secured login for each client POC. They see only their own backups,
              monitors, and SLA dashboards — never each other&apos;s, never your full estate.
            </p>
            <ul className="space-y-2 text-sm text-slate-400">
              {[
                "Per-client scoped views",
                "Read-only by design",
                "Their own TV / monitor page",
                "Email-based authentication",
              ].map((l) => (
                <li key={l} className="flex items-center gap-2"><CheckCircle weight="fill" size={14} className="text-brand-cyan"/>{l}</li>
              ))}
            </ul>
          </div>
          <div className="sw-card p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">CLIENT PORTAL — ACME MSP</div>
            <div className="font-display text-xl font-bold mb-4 text-white">Your backup status</div>
            <div className="space-y-2">
              {[
                { n: "fileserver01-daily",  s: "up",   sub: "Veeam · last run 2h ago" },
                { n: "sql-prod-hourly",     s: "up",   sub: "Datto · last run 12m ago" },
                { n: "exch-mbx-01-nightly", s: "down", sub: "Acronis · MISSED expected 24h" },
                { n: "ISP gateway · WAN1",  s: "up",   sub: "Ping · 12ms" },
              ].map((r) => (
                <div key={r.n} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2">
                  <div className="flex items-center gap-3">
                    <span className={`status-dot ${r.s}`} />
                    <div>
                      <div className="text-slate-100">{r.n}</div>
                      <div className="text-[11px] text-slate-500">{r.sub}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-slate-500">
        SENTRYWATCH // {new Date().getFullYear()} // status: <span className="text-emerald-400">all systems normal</span>
      </footer>
    </div>
  );
}
